import { FC, useCallback, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from 'reactstrap';
import { toast } from 'react-toastify';
import IntakeDropzone, {
  IntakeFileStatusRow,
} from '../../../ai-recruiter-console/IntakeDropzone';
import { VIEW_CANDIDATE_ROUTE } from '../../../../utils/constant';
import { BULK_ADD_BATCH_STATUS } from '../../gql';

// ---------------------------------------------------------------------------
// Props contract
// ---------------------------------------------------------------------------
export interface BulkAddModalProps {
  isOpen: boolean;
  jobOrderId: string;
  /**
   * Called after the modal closes. `completed` is true when a batch finished
   * (at least one file reached done/failed) during this open, so the host can
   * switch to the Applied filter / AI-score sort before reloading.
   */
  onClose: (result: { completed: boolean }) => void;
}

// ---------------------------------------------------------------------------
// API contract (POST /bulk-add/upload + bulkAddBatchStatus)
// ---------------------------------------------------------------------------

export interface BulkAddFileResult {
  fileId: string;
  fileName: string;
  status: 'pending' | 'processing' | 'done' | 'failed' | string;
  reason?: string | null;
  parser?: 'sovren' | 'claude' | string | null;
  candidateId?: string | null;
  candidateName?: string | null;
  candidateExisted?: boolean | null;
  jobApplicantId?: string | null;
  jobApplicantExisted?: boolean | null;
  jobAppStatus?: string | null;
  aiScore?: number | null;
}

const BULK_ADD_UPLOAD_PATH = '/bulk-add/upload';

const ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.txt',
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
  '.gif',
];
const ACCEPTED_MIMES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
];
const ACCEPTED_LABEL = 'PDF, DOC, DOCX, TXT, or image (PNG, JPG, WEBP, GIF)';

export const BULK_ADD_REASON_LABELS: Record<string, string> = {
  UNSUPPORTED_FILE: 'File type not supported.',
  PARSE_FAILED: 'Could not read the resume — the file may be corrupt or unreadable.',
  UPLOAD_FAILED: 'Upload to storage failed. Please try again.',
  DUPLICATE: 'Duplicate resume — already processed for this job order.',
  NO_NAME: 'No candidate name could be found in the resume.',
};

const friendlyReason = (code: string | null | undefined): string =>
  (code && BULK_ADD_REASON_LABELS[code]) ||
  'An unexpected error occurred. Please try again.';

// ---------------------------------------------------------------------------
// Sorting: done rows first (candidate name, then file name), failed rows last.
// ---------------------------------------------------------------------------

export const sortBulkAddResults = (rows: BulkAddFileResult[]): BulkAddFileResult[] =>
  [...rows].sort((a, b) => {
    const fa = a.status === 'failed' ? 1 : 0;
    const fb = b.status === 'failed' ? 1 : 0;
    if (fa !== fb) return fa - fb;
    const byName = (a.candidateName ?? '').localeCompare(b.candidateName ?? '');
    if (byName !== 0) return byName;
    return (a.fileName ?? '').localeCompare(b.fileName ?? '');
  });

const isTerminal = (r: BulkAddFileResult) => r.status === 'done' || r.status === 'failed';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const BulkAddModal: FC<BulkAddModalProps> = ({ isOpen, jobOrderId, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<BulkAddFileResult[] | null>(null);
  // Bumped on close so the next open starts from a fresh dropzone.
  const [runKey, setRunKey] = useState(0);

  const handleClose = useCallback(() => {
    if (busy) {
      toast.warn('Resumes are still being processed. Please wait until the batch finishes.');
      return;
    }
    const completed = !!results?.some(isTerminal);
    setResults(null);
    setRunKey((k) => k + 1);
    onClose({ completed });
  }, [busy, onClose, results]);

  const handleComplete = useCallback((rows: IntakeFileStatusRow[]) => {
    setResults(sortBulkAddResults(rows as BulkAddFileResult[]));
    setBusy(false);
  }, []);

  const extraFields = useMemo(() => ({ jobOrderId }), [jobOrderId]);

  const summary = useMemo(() => {
    const rows = results ?? [];
    return {
      added: rows.filter((r) => r.status === 'done' && !r.jobApplicantExisted).length,
      alreadyApplied: rows.filter((r) => r.status === 'done' && !!r.jobApplicantExisted)
        .length,
      failed: rows.filter((r) => r.status === 'failed').length,
    };
  }, [results]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      size="lg"
      className="revampDialog"
      backdrop={busy ? 'static' : true}
      keyboard={!busy}
    >
      <ModalHeader toggle={handleClose}>Bulk Add Applicants</ModalHeader>

      <ModalBody>
        {results === null ? (
          <>
            <p className="text-muted" style={{ fontSize: '0.875rem', marginBottom: 8 }}>
              Drop resumes to create candidates (or match existing ones by resume or
              email), add them to this job order as Applied, and score their AI fit.
            </p>
            <IntakeDropzone
              key={runKey}
              jobOrderId={jobOrderId}
              onClose={handleClose}
              uploadPath={BULK_ADD_UPLOAD_PATH}
              extraFields={extraFields}
              statusQuery={BULK_ADD_BATCH_STATUS}
              selectStatusFiles={(data) => data?.bulkAddBatchStatus}
              acceptedExtensions={ACCEPTED_EXTENSIONS}
              acceptedMimes={ACCEPTED_MIMES}
              acceptedLabel={ACCEPTED_LABEL}
              hintSuffix="added to this job order as Applied"
              completionText="Batch complete."
              reasonLabels={BULK_ADD_REASON_LABELS}
              showCloseButton={false}
              onBusyChange={setBusy}
              onComplete={handleComplete}
            />
          </>
        ) : (
          <>
            <p style={{ fontSize: '0.875rem', marginBottom: 12 }}>
              <strong>{summary.added}</strong> added &middot;{' '}
              <strong>{summary.alreadyApplied}</strong> already applied &middot;{' '}
              <strong className={summary.failed ? 'text-danger' : undefined}>
                {summary.failed}
              </strong>{' '}
              failed
            </p>

            {results.length === 0 ? (
              <p className="text-muted">No files were processed.</p>
            ) : (
              <div style={{ maxHeight: 460, overflowY: 'auto' }}>
                {/* Fixed layout + explicit widths: the app's global table CSS
                    sets white-space: nowrap on cells, so long file names would
                    otherwise overflow into the next column. */}
                <Table
                  className="mb-0"
                  size="sm"
                  style={{ fontSize: 13, tableLayout: 'fixed', width: '100%' }}
                >
                  <colgroup>
                    <col style={{ width: '28%' }} />
                    <col style={{ width: '42%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Candidate</th>
                      <th>File</th>
                      <th>New / Existing</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r) => {
                      const failed = r.status === 'failed';
                      const displayName = r.candidateName || r.fileName;
                      return (
                        <tr key={r.fileId}>
                          <td style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                            {r.candidateId ? (
                              <a
                                href={`${VIEW_CANDIDATE_ROUTE}/${r.candidateId}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {displayName}
                              </a>
                            ) : (
                              displayName
                            )}
                          </td>
                          <td
                            style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}
                            className="text-muted"
                            title={r.fileName}
                          >
                            {r.fileName}
                          </td>
                          <td>
                            {r.candidateExisted == null ? (
                              <span className="text-muted">—</span>
                            ) : r.candidateExisted ? (
                              'Existing'
                            ) : (
                              'New'
                            )}
                          </td>
                          <td style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                            {failed ? (
                              <>
                                <Badge color="danger">Failed</Badge>
                                <div className="text-danger" style={{ fontSize: 11 }}>
                                  {friendlyReason(r.reason)}
                                </div>
                              </>
                            ) : (
                              <Badge color="success">Done</Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={busy}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BulkAddModal;
